import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react';
import { memo, useState, useEffect, useCallback } from 'react';
import type { NavigationState, NavigationCallbacks } from './types';
import { LayoutMode } from './types';
import Logo from '../common/Logo';
import styles from './ReaderBottomToolbar.module.css';
import { useAlert } from '../../contexts/AlertContext';
import { useAppName, useAppLogoUrl } from '../../hooks/useTenantBranding';
import { useTranslation } from '../../i18n/useTranslation';

interface ReaderBottomToolbarProps {
  navigation: NavigationState;
  navigationCallbacks: NavigationCallbacks;
  showZoomControls?: boolean;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  onResetZoom?: () => void;
  zoomLevel?: number;
}

function ReaderBottomToolbar({
  navigation,
  navigationCallbacks,
  showZoomControls = false,
  onZoomIn,
  onZoomOut,
  onResetZoom,
  zoomLevel = 100,
}: ReaderBottomToolbarProps) {
  const { t } = useTranslation();
  const { showToast } = useAlert();
  const appName = useAppName();
  const appLogoUrl = useAppLogoUrl();

  // Prefer real page numbers from book.locations when available;
  // fall back to CFI-index-based numbering for formats (PDF, etc.) that populate currentLocation directly.
  const currentLocationNum =
    typeof navigation.currentPage === 'number'
      ? navigation.currentPage
      : typeof navigation.currentLocation === 'number'
      ? navigation.currentLocation + 1
      : 1;

  // End of the visible range. epub.js reports location.end on the relocated
  // event, but in Single mode each visible page already spans multiple
  // book.locations chunks (~1024 chars each), so an "end" marker further
  // than "start" isn't the same as "multiple pages visible." Only show the
  // range in Spread mode where two actual pages are on-screen simultaneously
  // — in Single / Scrolling the range would look like multi-page count to
  // the user when there's really just one visual page.
  const currentEndLocationNum =
    typeof navigation.currentEndPage === 'number'
      ? navigation.currentEndPage
      : currentLocationNum;
  const isScrolling = navigation.effectiveLayoutMode === LayoutMode.Scrolling;
  const isSpread = navigation.effectiveLayoutMode === LayoutMode.Spread;
  const isRange = isSpread && currentEndLocationNum > currentLocationNum;

  const totalCount =
    typeof navigation.totalPages === 'number'
      ? navigation.totalPages
      : navigation.totalLocations;

  const [sliderValue, setSliderValue] = useState(currentLocationNum);
  const [pageInputValue, setPageInputValue] = useState(String(currentLocationNum));
  const [isEditingPage, setIsEditingPage] = useState(false);

  // Sync slider with external navigation changes
  useEffect(() => {
    if (typeof navigation.currentLocation === 'number') {
      setSliderValue(navigation.currentLocation + 1);
    }
  }, [navigation.currentLocation]);

  // Keep input in sync with current page when not editing
  useEffect(() => {
    if (!isEditingPage) {
      setPageInputValue(String(currentLocationNum));
    }
  }, [currentLocationNum, isEditingPage]);

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(e.target.value, 10);
    setSliderValue(newValue);
  };

  const handleSliderCommit = () => {
    navigationCallbacks.onNavigateToLocation(sliderValue - 1);
  };

  const handlePageInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPageInputValue(e.target.value);
  }, []);

  const handlePageInputSubmit = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const pageNum = parseInt(pageInputValue, 10);
      if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= navigation.totalLocations) {
        navigationCallbacks.onNavigateToLocation(pageNum - 1);
        setIsEditingPage(false);
        (e.target as HTMLInputElement).blur();
      } else {
        showToast(t('App.Reader.BottomToolbar.InvalidPageWarning', { total: totalCount }), {
          type: 'warning',
        });
      }
    }
  }, [pageInputValue, navigation.totalLocations, navigationCallbacks, showToast]);

  const handlePageInputFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    setIsEditingPage(true);
    e.target.select();
  }, []);

  const handlePageInputBlur = useCallback(() => {
    setIsEditingPage(false);
    setPageInputValue(String(currentLocationNum));
  }, [currentLocationNum]);

  return (
    <div className={styles.toolbar}>
      {/* Chapter Name (Fixed Width - Prevents Slider Jump) */}
      <div className={styles.chapterName} title={navigation.currentLocationLabel}>
        {navigation.currentLocationLabel}
      </div>

      {/* Slider Container */}
      <div className={styles.sliderContainer}>
        <input
          type="range"
          min={1}
          max={navigation.totalLocations}
          value={sliderValue}
          onChange={handleSliderChange}
          onMouseUp={handleSliderCommit}
          onTouchEnd={handleSliderCommit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSliderCommit();
            }
          }}
          className={styles.slider}
          style={{
            '--progress-percentage': `${navigation.progressPercentage}%`
          } as React.CSSProperties}
          aria-label={t('App.Reader.BottomToolbar.NavigateToPage', { page: sliderValue, total: totalCount })}
          aria-valuemin={1}
          aria-valuemax={navigation.totalLocations}
          aria-valuenow={sliderValue}
          aria-valuetext={t('App.Reader.BottomToolbar.PageValueText', { page: sliderValue, total: totalCount })}
        />
      </div>

      {/* Page Number Input / Scrolling progress */}
      {isScrolling ? (
        <div className={styles.pageJump} aria-live="polite">
          <span className="text-xs text-neutral-600">
            {navigation.currentLocationLabel} · {Math.round(navigation.progressPercentage)}%
          </span>
        </div>
      ) : (
        <>
          {/* Page Number Input (shows current page, editable to navigate).
              In Spread mode both the left and right page indices are shown as
              a non-editable "6–7 / 815" label — editing a range isn't a useful
              input, and the slider still lets the admin jump anywhere. */}
          <div className={styles.pageJump}>
            {isRange ? (
              <span
                className={styles.pageInput}
                aria-label={t('App.Reader.BottomToolbar.PagesRange', { start: currentLocationNum, end: currentEndLocationNum, total: totalCount })}
                title={`${currentLocationNum}\u2013${currentEndLocationNum}`}
              >
                {currentLocationNum}&ndash;{currentEndLocationNum}
              </span>
            ) : (
              <input
                type="number"
                min={1}
                max={totalCount}
                value={pageInputValue}
                onChange={handlePageInputChange}
                onKeyDown={handlePageInputSubmit}
                onFocus={handlePageInputFocus}
                onBlur={handlePageInputBlur}
                className={styles.pageInput}
                aria-label={t('App.Reader.BottomToolbar.CurrentPage', { page: currentLocationNum, total: totalCount })}
                title={t('App.Reader.BottomToolbar.EditPageTitle')}
              />
            )}
            <span className={styles.pageTotal}>/ {totalCount}</span>
          </div>

          {/* Mobile-only page count (pageInput is hidden on mobile) */}
          <div className={styles.pageCount} aria-hidden="true">
            {isRange
              ? `${currentLocationNum}\u2013${currentEndLocationNum} / ${totalCount}`
              : `${currentLocationNum} / ${totalCount}`}
          </div>
        </>
      )}

      {/* Previous Button */}
      <button
        onClick={navigationCallbacks.onNavigatePrev}
        disabled={!navigation.canNavigatePrev}
        className={styles.navButton}
        aria-label={t('App.Reader.BottomToolbar.PreviousPage')}
        title={t('App.Reader.BottomToolbar.PreviousPage')}
      >
        <ChevronLeft className={styles.navButtonIcon} />
      </button>

      {/* Next Button */}
      <button
        onClick={navigationCallbacks.onNavigateNext}
        disabled={!navigation.canNavigateNext}
        className={styles.navButton}
        aria-label={t('App.Reader.BottomToolbar.NextPage')}
        title={t('App.Reader.BottomToolbar.NextPage')}
      >
        <ChevronRight className={styles.navButtonIcon} />
      </button>

      {/* Zoom Controls (PDF only) */}
      {showZoomControls && (
        <>
          <div className={styles.divider} />
          <div className={styles.zoomContainer}>
            <button
              onClick={onZoomOut}
              className={styles.zoomButton}
              aria-label={t('App.Reader.BottomToolbar.ZoomOut')}
              title={t('App.Reader.BottomToolbar.ZoomOut')}
            >
              <ZoomOut className={styles.zoomButtonIcon} />
            </button>
            <button
              onClick={onResetZoom}
              className={styles.zoomLevel}
              aria-label={t('App.Reader.BottomToolbar.ResetZoom')}
              title={t('App.Reader.BottomToolbar.ResetZoom')}
              aria-live="polite"
            >
              {zoomLevel}%
            </button>
            <button
              onClick={onZoomIn}
              className={styles.zoomButton}
              aria-label={t('App.Reader.BottomToolbar.ZoomIn')}
              title={t('App.Reader.BottomToolbar.ZoomIn')}
            >
              <ZoomIn className={styles.zoomButtonIcon} />
            </button>
          </div>
        </>
      )}

      {/* Progress Percentage */}
      <div className={styles.progress} aria-live="polite">
        {Math.round(navigation.progressPercentage)}%
      </div>

      {/* Logo - Full logo on desktop, hidden on mobile */}
      <div className={styles.logoContainer}>
        <Logo size="xs" variant="light" alt={appName} customLogoUrl={appLogoUrl} />
      </div>
    </div>
  );
}

export default memo(ReaderBottomToolbar);
